import * as React from 'react'

import { Pencil1Icon, CheckIcon, Cross1Icon } from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import { useBranchQuery } from '~/lib/branches/use-branch'
import { useBranchHref } from '../../../../../branch/_components/branch-actions-cta'
import { useApplySuggestion } from '../../../../../tab-content/suggestion/content'
import { useRouter } from 'next/navigation'
import { useMessageContext } from '../../../../'

export const Suggestion = ({
  status,
  message,
  branchId,
  description,
}: {
  status: string
  message: string
  branchId: string | undefined
  description: string | undefined
}) => {
  const router = useRouter()
  const { data: branch } = useBranchQuery({ id: branchId })
  const href = useBranchHref({ branch })
  const { mutate: accept } = useApplySuggestion(
    branchId ? { branchId } : undefined
  )
  const appliedInMainBranch = !branchId || branch?.inlineSuggestionAppliedAt
  const isError = status !== 'success'
  const { rootComment } = useMessageContext() ?? {}

  React.useLayoutEffect(() => {
    if (!branchId) return
    document
      .querySelectorAll(`[data-suggestion-branch-id="${branchId}"]`)
      .forEach((el, i, { length }) => {
        // hide all their Apply buttons except the last one.
        const isLast = i === length - 1
        if (isLast) return
        const applyButton = el.querySelector('[data-suggestion-apply-button]')
        if (applyButton) {
          applyButton.classList.add('hidden')
        }
      })
  }, [branchId])

  let bottomText = null
  if (isError) {
    bottomText = message
  } else if (description) {
    bottomText = description
  }

  const onReviewLink = (e: React.MouseEvent<HTMLButtonElement>) => {
    // for some reason, this link is opening in a new tab
    // we'll prevent default and handle it with router directly
    // unless the user is pressing command/control
    e.preventDefault()
    e.stopPropagation()

    const url = new URL(href, window.location.origin)
    if (rootComment) {
      url.searchParams.set(
        'back-after-apply',
        encodeURIComponent(
          window.location.pathname + `?commentId=${rootComment.id}`
        )
      )
    }

    if (e.metaKey) {
      // open in new tab and focus it
      const newTab = window.open(url.toString(), '_blank')
      if (newTab) newTab.focus()
      return
    }
    router.push(url.toString())
  }

  return (
    <SuggestionWrapper data-suggestion-branch-id={branchId}>
      <SuggestionContent
        className={clsx(bottomText && 'border-b border-dark-border-solid')}
      >
        <SuggestionHeader />
        {isError ? (
          <SuggestionError />
        ) : appliedInMainBranch ? (
          <button
            className="flex items-center gap-1 text-xs font-medium text-green-diff"
            onClick={onReviewLink}
            disabled={!branch}
          >
            <CheckIcon /> {!branch && 'Auto '}Applied
          </button>
        ) : (
          <SuggestionActions
            onReviewLink={onReviewLink}
            onAccept={() => accept()}
          />
        )}
      </SuggestionContent>
      {bottomText && <SuggestionFooter>{bottomText}</SuggestionFooter>}
    </SuggestionWrapper>
  )
}

export const LightSuggestion = ({
  status,
  message,
  branchId,
  description,
}: {
  status: string
  message: string
  branchId: string | undefined
  description: string | undefined
}) => {
  const isError = status !== 'success'

  let bottomText = null
  if (isError) {
    bottomText = message
  } else if (description) {
    bottomText = description
  }

  return (
    <SuggestionWrapper data-suggestion-branch-id={branchId}>
      <SuggestionContent
        className={clsx(bottomText && 'border-b border-dark-border-solid')}
      >
        <SuggestionHeader />
        {isError && <SuggestionError />}
      </SuggestionContent>
      {bottomText && <SuggestionFooter>{bottomText}</SuggestionFooter>}
    </SuggestionWrapper>
  )
}

const SuggestionHeader = ({ title = 'Suggestion' }: { title?: string }) => {
  return (
    <div className="flex items-center gap-1 text-xs font-medium text-dark-muted">
      <Pencil1Icon /> {title}
    </div>
  )
}

const SuggestionError = () => {
  return (
    <div className="flex items-center gap-1 text-xs font-medium text-red-diff">
      <Cross1Icon /> Error
    </div>
  )
}

const SuggestionFooter = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="px-2 py-1.5 text-xs text-dark-faint leading-tight font-medium bg-dark-base">
      {children}
    </div>
  )
}

const SuggestionContent = ({
  children,
  className,
}: {
  children: React.ReactNode
  className: string
}) => {
  return (
    <div
      className={clsx(
        'px-2 py-1.5 bg-dark-base-dark flex items-center justify-between',
        className
      )}
    >
      {children}
    </div>
  )
}

const SuggestionWrapper = ({
  children,
  ...rest
}: { children: React.ReactNode } & React.JSX.IntrinsicElements['div']) => {
  return (
    <div
      className="rounded-md border border-dark-border-solid overflow-hidden"
      {...rest}
    >
      {children}
    </div>
  )
}

const SuggestionActions = ({
  onReviewLink,
  onAccept,
}: {
  onReviewLink?: React.JSX.IntrinsicElements['button']['onClick']
  onAccept?: React.JSX.IntrinsicElements['button']['onClick']
}) => {
  return (
    <div className="flex items-center gap-0.5">
      {onReviewLink && (
        <button
          className="text-xs font-medium text-dark-dim hover:underline pr-1"
          onClick={onReviewLink}
        >
          Review
        </button>
      )}
      {onAccept && (
        <button
          className="text-xs font-medium text-dark-accent hover:underline pl-1"
          onClick={onAccept}
          data-suggestion-apply-button
        >
          Apply
        </button>
      )}
    </div>
  )
}
